import React from 'react';
import { useNavigate } from 'react-router-dom';

const C = {
  pri: '#0a0e27',
  pri2: '#2952CC',
  accent: '#6382eb',
  accent2: '#4f6ef2',
  bg: '#f7f8fc',
  white: '#FFFFFF',
  text: '#444',
  muted: '#666',
  border: '#eef0f5',
  green: '#10b981',
};

const S = {
  wrapper: {
    minHeight: '100vh',
    fontFamily: "'Inter','Segoe UI',system-ui,-apple-system,sans-serif",
    background: C.bg,
  },
  header: {
    background: `linear-gradient(135deg,${C.pri} 0%,#111c4e 50%,#1a2f7a 100%)`,
    padding: '48px 20px 64px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    color: 'rgba(255,255,255,0.70)',
    textDecoration: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    position: 'relative',
    zIndex: 1,
    marginBottom: '24px',
    background: 'none',
    border: 'none',
    padding: '0',
  },
  h1: {
    fontSize: 'clamp(28px,4vw,44px)',
    fontWeight: 800,
    color: C.white,
    margin: '0 0 12px',
    position: 'relative',
    zIndex: 1,
  },
  sub: {
    fontSize: '16px',
    color: 'rgba(255,255,255,0.55)',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: 1.7,
    position: 'relative',
    zIndex: 1,
  },
  body: { maxWidth: '900px', margin: '0 auto', padding: '48px 20px 80px' },
  card: {
    background: C.white,
    borderRadius: '16px',
    padding: '32px',
    border: `1px solid ${C.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    marginBottom: '24px',
  },
  h2: { fontSize: '22px', fontWeight: 700, color: C.pri, margin: '0 0 8px' },
  cardText: { fontSize: '15px', color: C.muted, lineHeight: 1.7 },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
    marginTop: '20px',
  },
  miniCard: {
    background: C.bg,
    borderRadius: '12px',
    padding: '20px',
    border: `1px solid ${C.border}`,
  },
  miniH3: { fontSize: '16px', fontWeight: 700, color: C.pri, margin: '0 0 6px' },
  miniP: { fontSize: '14px', color: C.muted, lineHeight: 1.6 },
  pill: (bg, c) => ({
    display: 'inline-block',
    padding: '5px 14px',
    borderRadius: '12px',
    background: bg,
    color: c,
    fontSize: '13px',
    fontWeight: 600,
  }),
  estadoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  estadoPill: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '8px',
    background: '#eef2ff',
    color: C.pri2,
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  arrow: { color: C.accent, fontWeight: 700, fontSize: '14px', margin: '0 4px' },
};

const ESTADOS = [
  {
    n: 1,
    nombre: 'Pendiente',
    quien: 'Bot crea el pedido',
    desc: 'El cliente armó el carrito y el bot registró el pedido en el sistema. Queda a la espera de revisión por el asesor.',
  },
  {
    n: 2,
    nombre: 'En Revisión',
    quien: 'Asesor/Admin revisa',
    desc: 'El asesor revisa los productos, cantidades, precios y disponibilidad. Puede modificar items o montos.',
  },
  {
    n: 3,
    nombre: 'Aprobado',
    quien: 'Asesor/Admin aprueba',
    desc: 'Monto aprobado. El pedido está listo para que el cliente proceda al pago. Se notifica al cliente con el total.',
  },
  {
    n: 4,
    nombre: 'Esperando Pago',
    quien: 'Cliente paga',
    desc: 'El cliente ve los métodos de pago disponibles para su sede, selecciona uno y recibe los datos bancarios.',
  },
  {
    n: 5,
    nombre: 'Pago en Verificación',
    quien: 'Cliente sube comprobante',
    desc: 'El cliente sube el comprobante de pago. El asesor recibe notificación para verificarlo.',
  },
  {
    n: 6,
    nombre: 'Verificado',
    quien: 'Asesor verifica pago',
    desc: 'Pago confirmado. El pedido pasa a preparación. Se registra método, referencia y monto.',
  },
  {
    n: 7,
    nombre: 'Armado',
    quien: 'Equipo prepara',
    desc: 'El equipo de la sede arma el pedido. Se verifica que todos los productos estén incluidos.',
  },
  {
    n: 8,
    nombre: 'Listo',
    quien: 'Listo para delivery',
    desc: 'Pedido armado y listo para ser recogido por el repartidor. Visible en el panel kanban.',
  },
  {
    n: 9,
    nombre: 'En Camino',
    quien: 'Delivery transporta',
    desc: 'El repartidor recogió el pedido y está en ruta. El cliente ve la ubicación en el mapa (próximamente GPS en vivo).',
  },
  {
    n: 10,
    nombre: 'Llegada',
    quien: 'Delivery confirma',
    desc: 'El repartidor confirma que llegó al destino. El cliente recibe notificación.',
  },
  {
    n: 11,
    nombre: 'Entregado',
    quien: 'Delivery/Admin confirma',
    desc: 'Pedido entregado al cliente. Se cierra el ciclo. El pedido queda en historial para consulta.',
  },
];

const METODOS = [
  {
    moneda: 'Dólares',
    metodos: [
      { n: 'Efectivo USD', d: 'Pago en dólares en efectivo al recibir el pedido o en la sede.' },
      {
        n: 'Binance',
        d: 'Transferencia USDT via Binance. Se proporciona ID o correo del negocio.',
      },
      { n: 'PayPal', d: 'Pago via PayPal. Incluye comisión configurable por sede.' },
      { n: 'Zelle', d: 'Transferencia via Zelle. Se proporciona correo asociado.' },
    ],
  },
  {
    moneda: 'Bolívares',
    metodos: [
      { n: 'Efectivo Bs', d: 'Pago en bolívares en efectivo. Calculado a la tasa del día.' },
      { n: 'Pago Móvil', d: 'Transferencia via Pago Móvil. Datos: cédula/RIF, teléfono, banco.' },
      {
        n: 'Transferencia',
        d: 'Transferencia bancaria nacional. Número de cuenta, propietario, cédula.',
      },
    ],
  },
  {
    moneda: 'Pesos Colombianos',
    metodos: [
      { n: 'Efectivo Pesos', d: 'Pago en pesos colombianos en efectivo.' },
      { n: 'Nequi', d: 'Transferencia via Nequi. Se solicita número de teléfono.' },
    ],
  },
];

function PedidosDetalle() {
  const nav = useNavigate();
  return (
    <div style={S.wrapper}>
      <style>{'body{overflow:auto!important}'}</style>
      <div style={S.header}>
        <button onClick={() => nav('/#pedidos')} style={S.backBtn}>
          &#8592; Volver al inicio
        </button>
        <h1 style={S.h1}>Pedidos y Métodos de Pago</h1>
        <p style={S.sub}>
          Trazabilidad total en cada paso del pedido, desde el registro hasta la entrega. 9 métodos
          de pago nativos, multi-moneda, configurables por sede.
        </p>
      </div>
      <div style={S.body}>
        <div style={S.card}>
          <h2 style={S.h2}>Ciclo de Vida del Pedido</h2>
          <p style={S.cardText}>
            Cada cambio registra: quien, cuando, desde que estado y hacia que estado. Cancelación o
            rechazo en cualquier punto, con motivo documentado.
          </p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '4px',
              marginTop: '20px',
              marginBottom: '24px',
            }}
          >
            {ESTADOS.map((e, i) => (
              <React.Fragment key={i}>
                <span style={S.estadoPill}>{e.nombre}</span>
                {i < ESTADOS.length - 1 && <span style={S.arrow}>&#8594;</span>}
              </React.Fragment>
            ))}
          </div>
          <div style={{ overflowX: 'auto' }}>
            {ESTADOS.map((e, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: '16px',
                  padding: '16px 0',
                  borderBottom: i < ESTADOS.length - 1 ? `1px solid ${C.border}` : 'none',
                }}
              >
                <div
                  style={{
                    ...S.pill('#eef2ff', C.pri2),
                    minWidth: '80px',
                    textAlign: 'center',
                    alignSelf: 'flex-start',
                  }}
                >
                  {e.n}
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{ fontSize: '16px', fontWeight: 700, color: C.pri, margin: '0 0 4px' }}
                  >
                    {e.nombre}
                  </h4>
                  <p style={{ fontSize: '13px', color: C.muted, margin: '0 0 4px' }}>
                    <strong>{e.quien}</strong>
                  </p>
                  <p style={{ fontSize: '14px', color: C.text, margin: 0, lineHeight: 1.5 }}>
                    {e.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={S.card}>
          <h2 style={S.h2}>9 Métodos de Pago Nativos</h2>
          <p style={S.cardText}>
            Multi-moneda. Cada sede configura sus propios datos bancarios, QR y referencias. Métodos
            adicionales personalizables.
          </p>
          {METODOS.map((m, i) => (
            <div key={i} style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: C.pri,
                  margin: '0 0 12px',
                  paddingBottom: '8px',
                  borderBottom: `2px solid ${C.accent}`,
                }}
              >
                {m.moneda}
              </h3>
              <div style={S.grid2}>
                {m.metodos.map((mp, j) => (
                  <div key={j} style={S.miniCard}>
                    <h4 style={S.miniH3}>{mp.n}</h4>
                    <p style={S.miniP}>{mp.d}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <h2 style={S.h2}>Métodos de Pago Personalizados</h2>
          <p style={S.cardText}>
            Además de los 9 métodos nativos, cada empresa puede crear sus propios métodos de pago
            con campos completamente configurables. Se define: nombre del método, campos requeridos
            al cliente (ej: "número de cuenta", "banco", "referencia"), si requiere comprobante, si
            tiene comisión, y los datos bancarios que se muestran al cliente.
          </p>
          <div style={S.grid2}>
            {[
              {
                t: 'Datos por sede',
                d: 'Cada sede configura sus propios datos bancarios. Una sede puede aceptar Pago Movil con una cuenta y otra sede con otra.',
              },
              {
                t: 'Comprobantes',
                d: 'El cliente sube comprobante de pago. El asesor verifica. Rechazo con motivo documentado si no es válido.',
              },
              {
                t: 'Comisión automática',
                d: 'Para métodos como PayPal, se configura porcentaje de comisión y recargo extra que se calcula automáticamente.',
              },
              {
                t: 'Conversión de moneda',
                d: 'Los montos se convierten automáticamente a la moneda del método seleccionado usando la tasa de cambio configurada.',
              },
            ].map((x, i) => (
              <div key={i} style={S.miniCard}>
                <h3 style={S.miniH3}>{x.t}</h3>
                <p style={S.miniP}>{x.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={S.card}>
          <h2 style={S.h2}>Confirmación Pendiente</h2>
          <p style={S.cardText}>
            Cuando el asesor modifica un pedido (cambia productos, precios o cantidades), se activa
            el mecanismo de <strong>confirmación pendiente</strong>. El cliente debe aceptar o
            rechazar los cambios antes de que el pedido continúe. Esto evita malentendidos y
            garantiza que ambas partes estén de acuerdo.
          </p>
        </div>
        <button
          onClick={() => nav('/#pedidos')}
          style={{
            ...S.backBtn,
            color: C.pri2,
            display: 'block',
            margin: '0 auto',
            fontSize: '16px',
            fontWeight: 600,
          }}
        >
          &#8592; Volver al inicio
        </button>
      </div>
    </div>
  );
}

export default PedidosDetalle;
